import { ClerkLoaded, SignIn, SignUp, UserProfile } from '@clerk/tanstack-react-start'
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { localizedPath, useTranslation } from '../lib/i18n'

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
const isClerkConfigured = Boolean(clerkPublishableKey)

// Self-branded embedded Clerk. Only theme-neutral values live here (font +
// square corners); every colour is themed via the `.cl-*` overrides in
// styles.css so light and dark both track the design tokens. The page itself
// renders the heading, so Clerk's own header is hidden via CSS.
const clerkAppearance = {
  variables: {
    // Mirrors the --font-sans stack in styles.css: the two metric-matched
    // fallback faces ('Montserrat Fallback Arial' / 'Montserrat Fallback
    // Roboto', each `local()`-only so one resolves per device) sit right after
    // Montserrat so Clerk's embedded form has the same zero-CLS swap behaviour
    // as the rest of the app. Clerk injects this into inline styles, so it
    // cannot read the CSS variable and the stack is spelled out here.
    fontFamily:
      "'Montserrat', 'Montserrat Fallback Arial', 'Montserrat Fallback Roboto', system-ui, -apple-system, sans-serif",
    fontWeight: { normal: 500, medium: 600, bold: 700 },
    borderRadius: '0px',
    colorPrimary: '#111317',
  },
  elements: {
    rootBox: 'aba-clerk-root',
    card: 'aba-clerk-card',
    footer: 'hidden',
    footerAction: 'hidden',
    footerItem: 'hidden',
  },
} as const

export function LoginPage() {
  const { locale, t, dict } = useTranslation()
  return (
    <AuthSplitShell
      eyebrow={t('pages.auth.loginEyebrow')}
      title={t('pages.auth.loginTitle')}
      subtitle={t('pages.auth.loginText')}
      proof={dict.pages.auth.loginProof}
      formHeading={t('pages.auth.loginFormHeading')}
      formSubheading={t('pages.auth.loginFormSubheading')}
      switchText={t('pages.auth.loginSwitchText')}
      switchHref={localizedPath('register', locale)}
      switchCta={t('common.signUpCta')}
    >
      {isClerkConfigured ? (
        <ClerkSlot fields={1}>
          <SignIn
            routing="path"
            path={localizedPath('login', locale)}
            signUpUrl={localizedPath('register', locale)}
            appearance={clerkAppearance}
          />
        </ClerkSlot>
      ) : (
        <AuthOfflineNotice mode="login" />
      )}
    </AuthSplitShell>
  )
}

export function RegisterPage() {
  const { locale, t, dict } = useTranslation()
  return (
    <AuthSplitShell
      eyebrow={t('pages.auth.registerEyebrow')}
      title={t('pages.auth.registerTitle')}
      subtitle={t('pages.auth.registerText')}
      proof={dict.pages.auth.registerProof}
      formHeading={t('pages.auth.registerFormHeading')}
      formSubheading={t('pages.auth.registerFormSubheading')}
      switchText={t('pages.auth.registerSwitchText')}
      switchHref={localizedPath('login', locale)}
      switchCta={t('pages.auth.registerSwitchCta')}
    >
      {isClerkConfigured ? (
        <ClerkSlot fields={2}>
          <SignUp
            routing="path"
            path={localizedPath('register', locale)}
            signInUrl={localizedPath('login', locale)}
            appearance={clerkAppearance}
          />
        </ClerkSlot>
      ) : (
        <AuthOfflineNotice mode="register" />
      )}
    </AuthSplitShell>
  )
}

export function ProfileSettingsPage() {
  const { t } = useTranslation()
  return (
    <main className="page-shell profile-page">
      <header className="profile-page-head">
        <span className="eyebrow">{t('pages.auth.profileEyebrow')}</span>
        <h1>{t('pages.auth.profileTitle')}</h1>
        <p>{t('pages.auth.profileText')}</p>
      </header>
      {isClerkConfigured ? (
        <UserProfile routing="hash" />
      ) : (
        <div className="auth-card">
          <AuthOfflineNotice mode="profile" />
        </div>
      )}
    </main>
  )
}

// The skeleton is rendered ALWAYS (not gated on Clerk's loading state) and
// stacked in the same grid cell as the widget. It is hidden purely in CSS the
// moment the real form DOM exists (`.auth-clerk-slot:has(.cl-card)`).
//
// Why not <ClerkLoading>: that unmounts the skeleton as soon as clerk-js
// reports "loaded", but the <SignIn> form needs another frame to mount its
// DOM - leaving a brief empty flash between the two. Keeping the skeleton
// mounted until `.cl-card` actually appears closes that gap. <ClerkLoaded>
// still gates the widget so it only mounts once Clerk is ready.
function ClerkSlot({
  fields,
  children,
}: {
  fields: number
  children: ReactNode
}) {
  return (
    <div className="auth-clerk-slot">
      <AuthFormSkeleton fields={fields} />
      <ClerkLoaded>{children}</ClerkLoaded>
    </div>
  )
}

// Skeleton that mirrors the Clerk form layout (social button, divider, one
// field per `fields` and submit) at matching heights and gaps so the card
// reserves the right space before Clerk renders.
function AuthFormSkeleton({ fields }: { fields: number }) {
  const { t } = useTranslation()
  return (
    <div className="auth-skeleton" aria-hidden="true">
      <div className="auth-skeleton-line auth-skeleton-social" />
      <div className="auth-skeleton-divider">
        <span />
        <small>{t('pages.auth.or')}</small>
        <span />
      </div>
      {Array.from({ length: fields }).map((_, index) => (
        <div className="auth-skeleton-field" key={index}>
          <div className="auth-skeleton-line auth-skeleton-label" />
          <div className="auth-skeleton-line auth-skeleton-input" />
        </div>
      ))}
      <div className="auth-skeleton-line auth-skeleton-submit" />
    </div>
  )
}

/**
 * Brand-grade split-screen shell for `/login` and `/register`.
 *
 * Left rail (hidden below `lg`): the Agentic School lime signal gradient with
 *   animated blobs, an eyebrow chip, the marketing title + subtitle, a 3-point
 *   proof checklist, a "Back to site" pill, and a Swiss-cross footnote. Below
 *   `lg` the rail is replaced by a compact mobile brand bar above the form.
 *
 * Right rail: a clean form area with a lime accent bar, heading + subheading,
 *   the self-branded embedded Clerk widget, and the switch-flow footer link.
 *
 * `data-hero-sentinel` is kept on the root so the shell's sticky-nav
 * IntersectionObserver contract in {@link AppShell} still resolves.
 */
function AuthSplitShell({
  eyebrow,
  title,
  subtitle,
  proof,
  formHeading,
  formSubheading,
  switchText,
  switchHref,
  switchCta,
  children,
}: {
  eyebrow: string
  title: string
  subtitle: string
  proof: ReadonlyArray<string>
  formHeading: string
  formSubheading: string
  switchText: string
  switchHref: string
  switchCta: string
  children: ReactNode
}) {
  const { locale, t } = useTranslation()
  const home = localizedPath('home', locale)
  const brandName = t('pages.auth.brandName')

  return (
    <div className="auth-split" data-hero-sentinel>
      {/* LEFT RAIL: brand showcase (hidden below lg). */}
      <aside className="auth-rail" aria-hidden="true">
        <div className="auth-rail-mesh" />
        <div className="auth-rail-blob auth-rail-blob-a" />
        <div className="auth-rail-blob auth-rail-blob-b" />

        <div className="auth-rail-top">
          <a className="auth-rail-brand" href={home} aria-label={brandName}>
            <img
              src="/wordmark.webp"
              alt=""
              className="brand-wordmark"
              width={508}
              height={56}
            />
          </a>
          <a className="auth-rail-back" href={home}>
            <ArrowLeft size={13} aria-hidden="true" />
            {t('pages.auth.backToSite')}
          </a>
        </div>

        <div className="auth-rail-body">
          <span className="auth-rail-chip">
            <Sparkles size={13} aria-hidden="true" />
            {eyebrow}
          </span>
          <h1 className="auth-rail-title">{title}</h1>
          <p className="auth-rail-sub">{subtitle}</p>
          <ul className="auth-rail-proof">
            {proof.map((line) => (
              <li key={line}>
                <span className="auth-rail-proof-icon" aria-hidden="true">
                  <Check size={13} strokeWidth={3} />
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="auth-rail-foot">
          <span className="auth-rail-swiss">
            <svg
              className="swiss-cross"
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <rect width="32" height="32" fill="#e11d2e" />
              <rect x="6" y="13" width="20" height="6" fill="#fff" />
              <rect x="13" y="6" width="6" height="20" fill="#fff" />
            </svg>
            {t('pages.auth.swissNote')}
          </span>
        </div>
      </aside>

      {/* RIGHT RAIL: auth form. */}
      <main className="auth-panel">
        {/* Mobile-only brand bar (lg+ uses the left rail instead). */}
        <div className="auth-panel-brandbar">
          <a href={home} className="brand-lockup" aria-label={brandName}>
            <img
              src="/wordmark.webp"
              alt=""
              className="brand-wordmark"
              width={508}
              height={56}
            />
          </a>
          <a className="auth-rail-back" href={home}>
            <ArrowLeft size={13} aria-hidden="true" />
            {t('pages.auth.backToSite')}
          </a>
        </div>

        <div className="auth-panel-inner">
          <div className="auth-panel-head">
            <span className="auth-panel-accent" aria-hidden="true" />
            <h2>{formHeading}</h2>
            <p>{formSubheading}</p>
          </div>
          <div className="auth-card">{children}</div>
          <p className="auth-switch">
            {switchText}{' '}
            <a href={switchHref}>
              {switchCta}
              <ArrowRight size={14} aria-hidden="true" />
            </a>
          </p>
        </div>
      </main>
    </div>
  )
}

function AuthOfflineNotice({
  mode,
}: {
  mode: 'login' | 'register' | 'profile'
}) {
  const { locale, t } = useTranslation()
  const heading =
    mode === 'profile'
      ? t('pages.auth.offlineProfileHeading')
      : t('pages.auth.offlineAuthHeading')
  const body =
    mode === 'profile'
      ? t('pages.auth.offlineProfileBody')
      : t('pages.auth.offlineAuthBody')
  return (
    <div className="auth-offline">
      <strong>{heading}</strong>
      <p>{body}</p>
      <a className="primary-button" href={localizedPath('home', locale)}>
        {t('pages.auth.toHome')}
        <ArrowRight size={16} />
      </a>
      <a className="ghost-button" href={localizedPath('courses', locale)}>
        {t('pages.auth.viewCourses')}
      </a>
    </div>
  )
}
